/**
 * Developer Dashboard – overview metrics + quick actions.
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getDashboardMetrics, getDeveloperTokenBalance } from '../services';
import { DashboardMetrics } from '../types';
import type { DeveloperTokenBalance } from '../services';
import {
  Activity,
  Users,
  Coins,
  Clock,
  CheckCircle2,
  AlertTriangle,
  ArrowRight,
  Key,
  BarChart3,
  Webhook,
  BookOpen,
  Wallet,
  Zap,
} from 'lucide-react';

export default function DashboardPage() {
  const { developer } = useAuth();
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [tokenBalance, setTokenBalance] = useState<DeveloperTokenBalance | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getDashboardMetrics(),
      getDeveloperTokenBalance(),
    ])
      .then(([m, t]) => {
        setMetrics(m);
        setTokenBalance(t);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="page-loader">Loading dashboard…</div>;

  return (
    <div className="dashboard-page">
      <div className="page-header">
        <div>
          <h1>Dashboard</h1>
          <p>Welcome back, {developer?.name ?? 'Developer'}</p>
        </div>
        <span className="tier-pill">{developer?.tier ?? 'free'}</span>
      </div>

      {/* ── Metric Cards ──────────────────────────────────── */}
      <div className="metrics-grid">
        <MetricCard icon={<Activity size={20} />} label="Requests Today" value={fmt(metrics?.requestsToday)} />
        <MetricCard icon={<Activity size={20} />} label="This Week" value={fmt(metrics?.requestsThisWeek)} />
        <MetricCard icon={<Activity size={20} />} label="This Month" value={fmt(metrics?.requestsThisMonth)} />
        <MetricCard icon={<Users size={20} />} label="Active Users" value={fmt(metrics?.activeUsers)} />
        <MetricCard icon={<Coins size={20} />} label="Tokens Consumed" value={fmt(metrics?.tokensConsumed)} />
        <MetricCard icon={<Clock size={20} />} label="Avg Latency" value={`${metrics?.averageLatencyMs ?? 0} ms`} />
        <MetricCard icon={<AlertTriangle size={20} />} label="Error Rate" value={`${((metrics?.errorRate ?? 0) * 100).toFixed(2)}%`} />
        <MetricCard icon={<CheckCircle2 size={20} />} label="Success Rate" value={`${(100 - ((metrics?.errorRate ?? 0) * 100)).toFixed(2)}%`} />
      </div>

      {/* ── Developer Test Token ──────────────────────────── */}
      <h2 className="section-title">Developer User Token</h2>
      <div className="dev-token-dashboard-card">
        <div className="dev-token-dashboard-icon">
          <Wallet size={22} />
        </div>
        <div className="dev-token-dashboard-body">
          {tokenBalance && tokenBalance.available > 0 ? (
            <>
              <div className="dev-token-balance">
                <Zap size={14} />
                <span className="dev-token-balance-value">
                  {fmt(tokenBalance.available)} tokens available
                </span>
              </div>
              <code className="dev-token-value">{tokenBalance.developerToken}</code>
            </>
          ) : (
            <p className="dev-token-empty">
              No test tokens yet — claim your free tokens to start testing LLM calls.
            </p>
          )}
        </div>
        <Link to="/dashboard/developer-tokens" className="dev-token-dashboard-cta">
          {tokenBalance && tokenBalance.available > 0 ? 'Manage' : 'Get Tokens'}
          <ArrowRight size={14} />
        </Link>
      </div>

      {/* ── Quick Actions ─────────────────────────────────── */}
      <h2 className="section-title">Quick Actions</h2>
      <div className="quick-actions">
        <QuickAction to="/dashboard/api-keys" icon={<Key size={20} />} label="Manage API Keys" />
        <QuickAction to="/dashboard/analytics" icon={<BarChart3 size={20} />} label="View Analytics" />
        <QuickAction to="/dashboard/webhooks" icon={<Webhook size={20} />} label="Configure Webhooks" />
        <QuickAction to="/dashboard/quickstart" icon={<BookOpen size={20} />} label="Quick Start Guide" />
      </div>
    </div>
  );
}

/* ── helpers ──────────────────────────────────────────── */

function fmt(n?: number): string {
  if (n === undefined) return '—';
  return n >= 1_000_000
    ? `${(n / 1_000_000).toFixed(1)}M`
    : n >= 1_000
    ? `${(n / 1_000).toFixed(1)}K`
    : n.toString();
}

function MetricCard({ icon, label, value, accent }: { icon: React.ReactNode; label: string; value: string; accent?: boolean }) {
  return (
    <div className={`metric-card ${accent ? 'accent' : ''}`}>
      <div className="metric-icon">{icon}</div>
      <div className="metric-body">
        <span className="metric-value">{value}</span>
        <span className="metric-label">{label}</span>
      </div>
    </div>
  );
}

function QuickAction({ to, icon, label }: { to: string; icon: React.ReactNode; label: string }) {
  return (
    <Link to={to} className="quick-action-card">
      {icon}
      <span>{label}</span>
      <ArrowRight size={16} />
    </Link>
  );
}
